import { Link } from '@inertiajs/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

/**
 * "Become a LuxRally Client" feature banner — a contained editorial transition
 * between the hero grid and Features that drives to /clients. Centered kicker /
 * Didot title / subtitle over a breathing video, with a "?" popover for the
 * property credit.
 */
export default function AIFeatureBanner() {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [infoOpen, setInfoOpen] = useState(false);

    useEffect(() => {
        videoRef.current?.play().catch(() => {});
    }, []);

    return (
        <div className="bg-[#fdfaf3] px-6 pt-4 pb-0 md:px-10 lg:pt-6">
            <section className="relative mx-auto w-full overflow-hidden rounded-sm" style={{ height: '24vh', maxWidth: '1200px' }}>
                <video
                    ref={videoRef}
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ animation: 'expandingHeroBreath 20s ease-in-out infinite' }}
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    poster="https://cdnweb.luxrallytravel.com/2026/03/7fd6ac1a-cc22-411f-8e24-c20b4c712833.png"
                >
                    <source
                        src="https://cdnweb.luxrallytravel.com/parity-migration/2025/08/de567095-10a8-4840-9a9b-42d6d5a16450.mp4"
                        type="video/mp4"
                    />
                </video>

                {/* Soft uniform scrim — protects the centered text without leaning heavy on any edge */}
                <div
                    className="absolute inset-0 z-[1]"
                    style={{
                        background: 'radial-gradient(ellipse at center, rgba(0, 0, 0, 0.45) 0%, rgba(0, 0, 0, 0.35) 55%, rgba(0, 0, 0, 0.25) 100%)',
                    }}
                />

                {/* Property credit — "?" info button + popover */}
                <div className="absolute right-4 bottom-4 z-20 md:right-6 md:bottom-6">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setInfoOpen((o) => !o);
                        }}
                        className={`group flex h-8 w-8 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm transition-all duration-300 hover:bg-white/20 ${infoOpen ? 'bg-white/20' : ''}`}
                    >
                        <span className="lux-didot text-sm text-white/80 transition-colors duration-300 group-hover:text-white">?</span>
                    </button>
                    <AnimatePresence>
                        {infoOpen && (
                            <>
                                <div className="fixed inset-0 z-40" onClick={() => setInfoOpen(false)} />
                                <motion.div
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 6 }}
                                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                    className="absolute right-0 bottom-full z-50 mb-3 w-72 border border-white/10 bg-[#0e2727]/90 p-5 backdrop-blur-xl"
                                >
                                    <h4 className="lux-didot lux-didot-sm mb-1.5 text-[#F5F5F5]">Rosewood Schloss Fuschl</h4>
                                    <p className="lux-caps lux-caps-sm mb-2 text-[#F5F5F5]/40">Salzburg, Austria</p>
                                    <p className="lux-body lux-body-sm text-[#F5F5F5]/70">A lakeside retreat nestled in the Austrian Alps.</p>
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>
                </div>

                {/* Centered content — tight vertical rhythm so container edges breathe */}
                <div className="absolute inset-0 z-10 flex items-center justify-center px-6 text-center md:px-10">
                    <div className="flex max-w-2xl flex-col items-center gap-1.5">
                        <span className="lux-caps lux-caps-sm text-[#F5F5F5]/65">The Smart Way to Travel</span>
                        <h2 className="lux-didot lux-didot-lg text-[#F5F5F5]">Become a LuxRally Client</h2>
                        <p className="lux-body lux-body-sm max-w-xl text-[#F5F5F5]/70">
                            The best of technology and human service — negotiated rates, no call centers, ever.
                        </p>
                        <Link
                            href="/clients"
                            className="lux-caps lux-caps-sm mt-3 inline-flex items-center gap-2 rounded-sm border border-white/30 bg-white/5 px-4 py-1.5 text-white backdrop-blur-sm transition-colors hover:border-white/50 hover:bg-white/10"
                        >
                            Get Booked
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
